import React, { lazy, Suspense, useEffect } from 'react';
import LoginOtp from "./LoginOtp";
import Layout from "./Layout.jsx";
import LoadingSpinner from "@/components/common/LoadingSpinner";
import { useAuth } from "@/auth/AuthContext";
import { CategoryFilterProvider } from "@/contexts/CategoryFilterContext";
import { ProductLabelSettingsProvider } from "@/contexts/ProductLabelSettingsContext";
import { PromotedCompaniesProvider } from "@/contexts/PromotedCompaniesContext";
import { BrowserRouter as Router, Route, Routes, useLocation, Navigate } from 'react-router-dom';
import AppUpdateWatcher from '@/components/common/AppUpdateWatcher';
import { getFirstAccessiblePage, hasModuleAccess, hasModuleEditAccess, isSystemAdminLevelUser } from "@/utils/systemModules";
import { useAreaManagerSession } from "@/contexts/AreaManagerSessionContext";
import { usePresenceHeartbeat } from "@/hooks/usePresenceHeartbeat";

// תפיסת שגיאת טעינת chunk (למשל באופליין) — מסך ידידותי במקום קריסה
class ChunkLoadErrorBoundary extends React.Component {
  state = { hasChunkError: false, fatalError: null };
  static getDerivedStateFromError(error) {
    if (error?.message?.includes?.('Failed to fetch dynamically imported module')) {
      return { hasChunkError: true, fatalError: null };
    }
    if (error?.name === 'ChunkLoadError' || error?.message?.includes?.('Loading chunk')) {
      return { hasChunkError: true, fatalError: null };
    }
    return { hasChunkError: false, fatalError: error };
  }
  componentDidUpdate(_prevProps, prevState) {
    if (this.state.hasChunkError && !prevState.hasChunkError) {
      const key = 'reshet-chunk-reload';
      if (!sessionStorage.getItem(key)) {
        sessionStorage.setItem(key, '1');
        window.location.reload();
      }
    }
  }
  componentDidCatch(error, info) {
    if (!error?.message?.includes?.('Failed to fetch dynamically imported module')) {
      console.error("[ChunkLoadErrorBoundary]", error, info);
    }
  }
  render() {
    if (this.state.fatalError) {
      return (
        <div style={{ padding: 24, textAlign: 'center', maxWidth: 420, margin: '40px auto' }}>
          <p style={{ marginBottom: 12, fontWeight: 600 }}>שגיאה בטעינת הדף</p>
          <p style={{ marginBottom: 16, fontSize: 14, color: '#666' }}>{this.state.fatalError.message}</p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            style={{ padding: '10px 20px', background: '#059669', color: 'white', border: 'none', borderRadius: 8, cursor: 'pointer' }}
          >
            רענן שוב
          </button>
        </div>
      );
    }
    if (this.state.hasChunkError) {
      return (
        <div style={{ padding: 24, textAlign: 'center', maxWidth: 400, margin: '40px auto' }}>
          <p style={{ marginBottom: 16 }}>הדף לא נטען — כנראה אין חיבור לאינטרנט והדף לא נשמר במטמון.</p>
          <p style={{ marginBottom: 16, fontSize: 14, color: '#666' }}>חבר לאינטרנט ולחץ רענן.</p>
          <button
            type="button"
            onClick={() => { if (typeof navigator !== 'undefined' && navigator.onLine) window.location.reload(); }}
            style={{ padding: '10px 20px', background: '#059669', color: 'white', border: 'none', borderRadius: 8, cursor: 'pointer' }}
          >
            רענן שוב
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// טעינה בשקט של chunks קריטיים לניווט בסניף — כדי שלא יחכו להורדה בכל לחיצה ברשת חלשה
function PrefetchCriticalChunks() {
  useEffect(() => {
    if (typeof navigator === "undefined" || !navigator.onLine) return;
    const warm = [
      import("./Home"),
      import("./Cart"),
      import("./Categories"),
      import("./CategoryProducts.jsx"),
      import("./StoreProducts.jsx"),
      import("./Companies"),
      import("./CompanyProducts"),
      import("./ProductDetails"),
      import("./Promotions"),
    ];
    Promise.allSettled(warm.map((p) => p.catch(() => {})));
  }, []);
  return null;
}

const Home = lazy(() => import("./Home"));
const Categories = lazy(() => import("./Categories"));
const Companies = lazy(() => import("./Companies"));
const Suppliers = lazy(() => import("./Suppliers"));
const Promotions = lazy(() => import("./Promotions"));
const Recommendations = lazy(() => import("./Recommendations"));
const MustOrder = lazy(() => import("./MustOrder"));
const NewProducts = lazy(() => import("./NewProducts"));
const ServiceRequests = lazy(() => import("./ServiceRequests"));
const DailyInventory = lazy(() => import("./DailyInventory"));
const ManagementInventory = lazy(() => import("./ManagementInventory"));
const Notifications = lazy(() => import("./Notifications"));
const Destructions = lazy(() => import("./Destructions"));
const Returns = lazy(() => import("./Returns"));
const Cart = lazy(() => import("./Cart"));
const ProductDetails = lazy(() => import("./ProductDetails"));
const CategoryProducts = lazy(() => import("./CategoryProducts.jsx"));
const CompanyProducts = lazy(() => import("./CompanyProducts"));
const SupplierProducts = lazy(() => import("./SupplierProducts"));
const InventoryCount = lazy(() => import("./InventoryCount"));
const Orders = lazy(() => import("./Orders"));
const OrderDetails = lazy(() => import("./OrderDetails"));
const ReceiveGoods = lazy(() => import("./ReceiveGoods"));
const ReceiveGoodsDetails = lazy(() => import("./ReceiveGoodsDetails"));
const InvoiceScan = lazy(() => import("./InvoiceScan"));
const InvoiceScanAdmin = lazy(() => import("./InvoiceScanAdmin"));
const Distributions = lazy(() => import("./Distributions"));
const Brochures = lazy(() => import("./Brochures"));
const PromotionImplementation = lazy(() => import("./PromotionImplementation"));
const ServiceRequestDetails = lazy(() => import("./ServiceRequestDetails"));
const DestructionDetails = lazy(() => import("./DestructionDetails"));
const ReturnDetails = lazy(() => import("./ReturnDetails"));
const SupplierRecommendations = lazy(() => import("./SupplierRecommendations"));
const Users = lazy(() => import("./Users"));
const UserDetails = lazy(() => import("./UserDetails"));
const SlowMovingProducts = lazy(() => import("./SlowMovingProducts"));
const Stores = lazy(() => import("./Stores"));
const StoreDetails = lazy(() => import("./StoreDetails"));
const Chains = lazy(() => import("./Chains"));
const MustOrderStores = lazy(() => import("./MustOrderStores"));
const AllSlowMovingProducts = lazy(() => import("./AllSlowMovingProducts"));
const SearchedNotInWarehouse = lazy(() => import("./SearchedNotInWarehouse"));
const AllReturns = lazy(() => import("./AllReturns"));
const AllDestructions = lazy(() => import("./AllDestructions"));
const Products = lazy(() => import("./Products"));
const ProductForm = lazy(() => import("./ProductForm"));
const StoreProducts = lazy(() => import("./StoreProducts.jsx"));
const StoreRecommendations = lazy(() => import("./StoreRecommendations"));
const CreateMustOrderTask = lazy(() => import("./CreateMustOrderTask"));
const ManageInventoryCounts = lazy(() => import("./ManageInventoryCounts"));
const CreateInventoryCount = lazy(() => import("./CreateInventoryCount"));
const SupplierDetails = lazy(() => import("./SupplierDetails"));
const StoresByChainPromotions = lazy(() => import("./StoresByChainPromotions"));
const PromotionsByStore = lazy(() => import("./PromotionsByStore"));
const SafeCount = lazy(() => import("./SafeCount"));
const SafeCountAdmin = lazy(() => import("./SafeCountAdmin"));
const SystemSettings = lazy(() => import("./SystemSettings"));
const CartManagement = lazy(() => import("./CartManagement"));
const PublicOrderView = lazy(() => import("./PublicOrderView"));
const DiagnosticsRun = lazy(() => import("./DiagnosticsRun"));
const ManagementTasks = lazy(() => import("./ManagementTasks"));
const ManagementTaskDetails = lazy(() => import("./ManagementTaskDetails"));
const StoreManagementTasks = lazy(() => import("./StoreManagementTasks"));
const CreateManagementTask = lazy(() => import("./CreateManagementTask"));
const CompleteManagementTask = lazy(() => import("./CompleteManagementTask"));
const BasicProductsReminder = lazy(() => import("./BasicProductsReminder"));
const AdminTerminal = lazy(() => import("./AdminTerminal"));
const PrefetchOrder = lazy(() => import("./PrefetchOrder"));
const CriticalData = lazy(() => import("./CriticalData"));
const AreaManagerStores = lazy(() => import("./AreaManagerStores"));
const AreaManagerOrders = lazy(() => import("./AreaManagerOrders"));
const AreaManagerSafeCounts = lazy(() => import("./AreaManagerSafeCounts"));
const SalesAnalytics = lazy(() => import("./SalesAnalytics"));
const StockSales = lazy(() => import("./StockSales"));
const SalesPeriodAnalysis = lazy(() => import("./SalesPeriodAnalysis"));
const SystemHealth = lazy(() => import("./SystemHealth"));
const KeshetHeroes = lazy(() => import("./KeshetHeroes"));
const StoreComaxCatalog = lazy(() => import("./StoreComaxCatalog.jsx"));
const SystemUpdates = lazy(() => import("./SystemUpdates"));
const SystemUpdateForm = lazy(() => import("./SystemUpdateForm"));

const PAGES = {
    Home: Home,
    Categories: Categories,
    Companies: Companies,
    Suppliers: Suppliers,
    Promotions: Promotions,
    Recommendations: Recommendations,
    MustOrder: MustOrder,
    NewProducts: NewProducts,
    ServiceRequests: ServiceRequests,
    DailyInventory: DailyInventory,
    ManagementInventory: ManagementInventory,
    Notifications: Notifications,
    Destructions: Destructions,
    Returns: Returns,
    Cart: Cart,
    ProductDetails: ProductDetails,
    CategoryProducts: CategoryProducts,
    CompanyProducts: CompanyProducts,
    SupplierProducts: SupplierProducts,
    InventoryCount: InventoryCount,
    Orders: Orders,
    OrderDetails: OrderDetails,
    ReceiveGoods: ReceiveGoods,
    ReceiveGoodsDetails: ReceiveGoodsDetails,
    InvoiceScan: InvoiceScan,
    InvoiceScanAdmin: InvoiceScanAdmin,
    Distributions: Distributions,
    Brochures: Brochures,
    PromotionImplementation: PromotionImplementation,
    ServiceRequestDetails: ServiceRequestDetails,
    DestructionDetails: DestructionDetails,
    ReturnDetails: ReturnDetails,
    SupplierRecommendations: SupplierRecommendations,
    Users: Users,
    UserDetails: UserDetails,
    SlowMovingProducts: SlowMovingProducts,
    Stores: Stores,
    StoreDetails: StoreDetails,
    Chains: Chains,
    MustOrderStores: MustOrderStores,
    AllSlowMovingProducts: AllSlowMovingProducts,
    SearchedNotInWarehouse: SearchedNotInWarehouse,
    AllReturns: AllReturns,
    AllDestructions: AllDestructions,
    Products: Products,
    ProductForm: ProductForm,
    StoreProducts: StoreProducts,
    StoreRecommendations: StoreRecommendations,
    CreateMustOrderTask: CreateMustOrderTask,
    ManageInventoryCounts: ManageInventoryCounts,
    CreateInventoryCount: CreateInventoryCount,
    SupplierDetails: SupplierDetails,
    StoresByChainPromotions: StoresByChainPromotions,
    PromotionsByStore: PromotionsByStore,
    SafeCount: SafeCount,
    SafeCountAdmin: SafeCountAdmin,
    SystemSettings: SystemSettings,
    CartManagement: CartManagement,
    BasicProductsReminder: BasicProductsReminder,
    AdminTerminal: AdminTerminal,
    PrefetchOrder: PrefetchOrder,
    ManagementTasks: ManagementTasks,
  ManagementTaskDetails: ManagementTaskDetails,
  StoreManagementTasks: StoreManagementTasks,
  CreateManagementTask: CreateManagementTask,
    CompleteManagementTask: CompleteManagementTask,
    CriticalData: CriticalData,
    AreaManagerStores: AreaManagerStores,
    AreaManagerOrders: AreaManagerOrders,
    AreaManagerSafeCounts: AreaManagerSafeCounts,
    SalesAnalytics: SalesAnalytics,
    StockSales: StockSales,
    SalesPeriodAnalysis: SalesPeriodAnalysis,
    SystemHealth: SystemHealth,
    KeshetHeroes: KeshetHeroes,
    StoreComaxCatalog: StoreComaxCatalog,
    SystemUpdates: SystemUpdates,
    SystemUpdateForm: SystemUpdateForm,
}

function _getCurrentPage(url) {
    if (url.endsWith('/')) {
        url = url.slice(0, -1);
    }
    let urlLastPart = url.split('/').pop();
    if (urlLastPart.includes('?')) {
        urlLastPart = urlLastPart.split('?')[0];
    }

    const pageName = Object.keys(PAGES).find(page => page.toLowerCase() === urlLastPart.toLowerCase());
    return pageName || urlLastPart || "Home";
}

// זה הרכיב ששומר עלינו. אם אין טוקן - הוא זורק ללוגין
function ProtectedRoute({ children }) {
    const token = localStorage.getItem("auth_token");

    if (!token) {
        // שימוש ב-Navigate עדיף על window.location כי הוא לא מרענן את כל הדף
        return <Navigate to="/login" replace />;
    }

    return children;
}

function PagesContent() {
    const { user } = useAuth();
    const { isActingAsStore } = useAreaManagerSession();
    const location = useLocation();
    usePresenceHeartbeat();

    // חריגה: אם אנחנו כבר בנתיב של לוגין, תציג אותו בלי Layout ובלי הגנות
    if (location.pathname === "/login") {
        return (
            <Routes>
                <Route path="/login" element={<LoginOtp />} />
            </Routes>
        );
    }

    if (location.pathname.startsWith("/view-order/")) {
        return (
            <Routes>
                <Route path="/view-order/:id" element={<PublicOrderView />} />
            </Routes>
        );
    }

    if (location.pathname.startsWith("/diagnostics/")) {
        return (
            <Routes>
                <Route path="/diagnostics/:token" element={<DiagnosticsRun />} />
            </Routes>
        );
    }
    
    const currentPage = location.pathname.startsWith("/SystemUpdates")
        ? "SystemUpdates"
        : _getCurrentPage(location.pathname);
    const isAdmin = user?.isSuperAdmin || user?.role?.key === 'SYSTEM_ADMIN' || user?.role?.key === 'SUPER_ADMIN';
    const isSuperAdminLevel =
        user?.isSuperAdmin ||
        user?.role?.key === 'SUPER_ADMIN' ||
        user?.role?.key === 'SYSTEM_ADMIN' ||
        user?.custom_role === 'system_admin';
    const isStrictSuperAdmin =
        user?.isSuperAdmin === true ||
        user?.role?.key === 'SUPER_ADMIN';

    const canAccessCriticalData =
        isSuperAdminLevel && !!user?.criticalDataAccess;

    const roleKey = user?.role?.key || user?.role;
    const isAreaManagerRole =
        roleKey === "AREA_MANAGER" ||
        user?.custom_role === "area_manager" ||
        user?.custom_role === "AREA_MANAGER";
    const canAccessDistributions =
        user?.isSuperAdmin === true ||
        user?.role?.key === "SUPER_ADMIN" ||
        (isSystemAdminLevelUser(user) && hasModuleEditAccess(user, "distributions"));

    const canAccessSalesAnalytics = isAdmin || isAreaManagerRole || !!user?.store_id;
const isPromotionsManager = roleKey === 'PROMOTIONS_ADMIN';
const isHrManagerRole = roleKey === 'HR_MANAGER';
const isMaintenanceManagerRole =
  roleKey === "MAINTENANCE_MANAGER" ||
  user?.custom_role === "MAINTENANCE_MANAGER" ||
  user?.custom_role === "maintenance_manager";
const landingPage = getFirstAccessiblePage(user);
const homeElement = isActingAsStore || hasModuleAccess(user, "home") ? <Home /> : <Navigate to={`/${landingPage}`} replace />;

// אם המשתמשת היא מנהלת מבצעים, אנחנו מגדירים רשימת נתיבים מצומצמת מאוד
if (isPromotionsManager) {
    return (
        <ProtectedRoute>
            <ChunkLoadErrorBoundary>
            <Layout currentPageName={currentPage}>
                <Suspense fallback={<LoadingSpinner />}>
                    <Routes>
                        <Route path="/Promotions" element={<Promotions />} />
                        <Route path="*" element={<Navigate to="/Promotions" replace />} />
                    </Routes>
                </Suspense>
            </Layout>
            </ChunkLoadErrorBoundary>
        </ProtectedRoute>
    );
}

if (isMaintenanceManagerRole) {
    return (
        <ProtectedRoute>
            <ChunkLoadErrorBoundary>
            <Layout currentPageName={currentPage}>
                <Suspense fallback={<LoadingSpinner />}>
                    <Routes>
                        <Route path="/ServiceRequests" element={<ServiceRequests />} />
                        <Route path="/ServiceRequestDetails" element={<ServiceRequestDetails />} />
                        <Route path="/Notifications" element={<Notifications />} />
                        <Route path="/" element={<Navigate to="/ServiceRequests" replace />} />
                        <Route path="/Home" element={<Navigate to="/ServiceRequests" replace />} />
                        <Route path="*" element={<Navigate to="/ServiceRequests" replace />} />
                    </Routes>
                </Suspense>
            </Layout>
            </ChunkLoadErrorBoundary>
        </ProtectedRoute>
    );
}

if (isHrManagerRole) {
    const canAccessNotifications = hasModuleAccess(user, "notifications");
    return (
        <ProtectedRoute>
            <ChunkLoadErrorBoundary>
            <Layout currentPageName={currentPage}>
                <Suspense fallback={<LoadingSpinner />}>
                    <Routes>
                        <Route path="/Users" element={<Users />} />
                        <Route path="/UserDetails" element={<UserDetails />} />
                        {canAccessNotifications ? (
                          <Route path="/Notifications" element={<Notifications />} />
                        ) : null}
                        <Route path="/" element={<Navigate to="/Users" replace />} />
                        <Route path="/Home" element={<Navigate to="/Users" replace />} />
                        <Route path="*" element={<Navigate to="/Users" replace />} />
                    </Routes>
                </Suspense>
            </Layout>
            </ChunkLoadErrorBoundary>
        </ProtectedRoute>
    );
}

const isMerlogManager = roleKey === 'MERLOG_MANAGER';

if (isMerlogManager) {
    return (
        <ProtectedRoute>
            <ChunkLoadErrorBoundary>
            <Layout currentPageName={currentPage}>
                <Suspense fallback={<LoadingSpinner />}>
                    <Routes>
                        <Route path="/Suppliers" element={<Suppliers />} />
                        <Route path="/SupplierDetails" element={<SupplierDetails />} />
                        <Route path="/Orders" element={<Orders />} />
                        <Route path="/OrderDetails" element={<OrderDetails />} />
                        <Route path="/Stores" element={<Stores />} />
                        <Route path="/StoreDetails" element={<StoreDetails />} />
                        <Route path="/" element={<Navigate to="/Suppliers" replace />} />
                        <Route path="/Home" element={<Navigate to="/Suppliers" replace />} />
                        <Route path="*" element={<Navigate to="/Suppliers" replace />} />
                    </Routes>
                </Suspense>
            </Layout>
            </ChunkLoadErrorBoundary>
        </ProtectedRoute>
    );
}

    return (
        <ProtectedRoute>
            <ProductLabelSettingsProvider>
            <PromotedCompaniesProvider>
            <CategoryFilterProvider>
            <ChunkLoadErrorBoundary>
            <PrefetchCriticalChunks />
            <Layout currentPageName={currentPage}>
                <Suspense fallback={<LoadingSpinner />}>
                    <Routes>
                    <Route path="/" element={<Navigate to={`/${landingPage}`} replace />} />
                    <Route path="/Home" element={homeElement} />
                    <Route path="/AreaManagerStores" element={<AreaManagerStores />} />
                    <Route path="/AreaManagerOrders" element={<AreaManagerOrders />} />
                    <Route path="/AreaManagerSafeCounts" element={<AreaManagerSafeCounts />} />
                    <Route path="/prefetch-order" element={<PrefetchOrder />} />
                    <Route path="/Categories" element={<Categories />} />
                    <Route path="/Companies" element={<Companies />} />
                    <Route path="/Suppliers" element={<Suppliers />} />
                    <Route path="/Promotions" element={<Promotions />} />
                    <Route path="/Recommendations" element={<Recommendations />} />
                    <Route path="/MustOrder" element={<MustOrder />} />
                    <Route path="/NewProducts" element={<NewProducts />} />
                    <Route path="/ServiceRequests" element={<ServiceRequests />} />
                    <Route path="/DailyInventory" element={<DailyInventory />} />
                    <Route path="/ManagementInventory" element={<ManagementInventory />} />
                    <Route path="/Notifications" element={<Notifications />} />
                    <Route path="/Destructions" element={<Destructions />} />
                    <Route path="/Returns" element={<Returns />} />
                    <Route path="/Cart" element={<Cart />} />
                    <Route path="/ProductDetails" element={<ProductDetails />} />
                    <Route path="/CategoryProducts" element={<CategoryProducts />} />
                    <Route path="/CompanyProducts" element={<CompanyProducts />} />
                    <Route path="/SupplierProducts" element={<SupplierProducts />} />
                    <Route path="/InventoryCount" element={<InventoryCount />} />
                    <Route path="/Orders" element={<Orders />} />
                    <Route path="/OrderDetails" element={<OrderDetails />} />
                    <Route path="/ReceiveGoods" element={<ReceiveGoods />} />
                    <Route path="/ReceiveGoodsDetails" element={<ReceiveGoodsDetails />} />
                    <Route path="/InvoiceScan" element={<InvoiceScan />} />
                    <Route
                        path="/InvoiceScanAdmin"
                        element={isStrictSuperAdmin ? <InvoiceScanAdmin /> : <Navigate to="/Home" replace />}
                    />
                    <Route
                        path="/Distributions"
                        element={canAccessDistributions ? <Distributions /> : <Navigate to="/Home" replace />}
                    />
                    <Route path="/Brochures" element={<Brochures />} />
                    <Route path="/PromotionImplementation" element={<PromotionImplementation />} />
                    <Route path="/ServiceRequestDetails" element={<ServiceRequestDetails />} />
                    <Route path="/DestructionDetails" element={<DestructionDetails />} />
                    <Route path="/ReturnDetails" element={<ReturnDetails />} />
                    <Route path="/SupplierRecommendations" element={<SupplierRecommendations />} />
                    <Route path="/Users" element={<Users />} />
                    <Route path="/UserDetails" element={<UserDetails />} />
                    <Route path="/SlowMovingProducts" element={<SlowMovingProducts />} />
                    <Route path="/Stores" element={<Stores />} />
                    <Route path="/StoreDetails" element={<StoreDetails />} />
                    <Route path="/Chains" element={<Chains />} />
                    <Route path="/MustOrderStores" element={<MustOrderStores />} />
                    <Route path="/AllSlowMovingProducts" element={<AllSlowMovingProducts />} />
                    <Route path="/SearchedNotInWarehouse" element={<SearchedNotInWarehouse />} />
                    <Route path="/AllReturns" element={<AllReturns />} />
                    <Route path="/AllDestructions" element={<AllDestructions />} />
                    <Route path="/Products" element={<Products />} />
                    <Route path="/ProductForm" element={<ProductForm />} />
                    <Route path="/StoreProducts" element={<StoreProducts />} />
                    <Route path="/StoreRecommendations" element={<StoreRecommendations />} />
                    <Route path="/CreateMustOrderTask" element={<CreateMustOrderTask />} />
                    <Route path="/ManageInventoryCounts" element={<ManageInventoryCounts />} />
                    <Route path="/CreateInventoryCount" element={<CreateInventoryCount />} />
                    <Route path="/SupplierDetails" element={<SupplierDetails />} />
                    <Route path="/StoresByChainPromotions" element={<StoresByChainPromotions />} />
                    <Route path="/PromotionsByStore" element={<PromotionsByStore />} />
                    <Route path="/SafeCount" element={<SafeCount />} />
                    <Route path="/SafeCountAdmin" element={<SafeCountAdmin />} />
                    <Route 
                        path="/SystemSettings" 
                        element={isAdmin ? <SystemSettings /> : <Navigate to="/" replace />} 
                    />
                    <Route
                        path="/SystemHealth"
                        element={isStrictSuperAdmin ? <SystemHealth /> : <Navigate to="/" replace />}
                    />
                    <Route
                        path="/KeshetHeroes"
                        element={isStrictSuperAdmin ? <KeshetHeroes /> : <Navigate to="/" replace />}
                    />
                    <Route
                        path="/StoreComaxCatalog"
                        element={isAdmin ? <StoreComaxCatalog /> : <Navigate to="/" replace />}
                    />
                    <Route path="/cartmanagement" element={<CartManagement />} />
                    <Route path="/SystemUpdates" element={isAdmin ? <SystemUpdates /> : <Navigate to="/" replace />} />
                    <Route path="/SystemUpdates/new" element={isAdmin ? <SystemUpdateForm /> : <Navigate to="/" replace />} />
                    <Route path="/SystemUpdates/:id" element={isAdmin ? <SystemUpdateForm /> : <Navigate to="/" replace />} />
                    <Route path="/ManagementTasks" element={isAdmin ? <ManagementTasks /> : <Navigate to="/" replace />} />                    
                    <Route path="/ManagementTaskDetails" element={isAdmin ? <ManagementTaskDetails /> : <Navigate to="/" replace />} />
                    <Route path="/StoreManagementTasks" element={<StoreManagementTasks />} />
                    <Route path="/CreateManagementTask" element={isAdmin ? <CreateManagementTask /> : <Navigate to="/" replace />} />
                    <Route path="/CompleteManagementTask" element={<CompleteManagementTask />} />
                    <Route path="/BasicProductsReminder" element={<BasicProductsReminder />} />
                    <Route path="/basicproductsreminder" element={<BasicProductsReminder />} />
                    <Route 
                        path="/AdminTerminal" 
                        element={isAdmin ? <AdminTerminal /> : <Navigate to="/" replace />} 
                    />
                    <Route
                        path="/CriticalData"
                        element={canAccessCriticalData ? <CriticalData /> : <Navigate to="/Home" replace />}
                    />
                    <Route
                        path="/SalesAnalytics"
                        element={canAccessSalesAnalytics ? <SalesAnalytics /> : <Navigate to="/" replace />}
                    />
                    <Route
                        path="/StockSales"
                        element={isAdmin || isAreaManagerRole ? <StockSales /> : <Navigate to="/" replace />}
                    />
                    <Route
                        path="/SalesPeriodAnalysis"
                        element={canAccessSalesAnalytics ? <SalesPeriodAnalysis /> : <Navigate to="/" replace />}
                    />
                </Routes>
                </Suspense>
            </Layout>
            </ChunkLoadErrorBoundary>
            </CategoryFilterProvider>
            </PromotedCompaniesProvider>
            </ProductLabelSettingsProvider>
        </ProtectedRoute>
    );
}

export default function Pages() {
    return (
        // בלי startTransition — אחרת לחיצה על מבצעים/קטגוריות משנה URL אבל משאירה את המסך הקודם עד שה-chunk נטען (נראה "לא זז")
        <Router unstable_useTransitions={false}>
            <AppUpdateWatcher />
            <PagesContent />
        </Router>
    );
}